import { Editor } from "codemirror";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { UnControlled as CodeMirror } from "react-codemirror2";
import { LoadingOverlay, RenderWhen } from "../../../components";
import { MenuItem } from "../../../components/@types";
import {
  useEmailPreview,
  useGetSampleAnalysisStatus,
  usePreviewSampleData,
  useReanalyze,
  useYaraStringMatches
} from "../hooks";
import VisualizationLoadingHandler from "./visualization/VisualizationLoadingHandler";
import YaraStringMatchControls from "./yarastringmatch/YaraStringMatchControls";
import {
  calculateTextMarks,
  getCodeMirrorMode,
  getFilterOptions,
  getMenuItemFromString
} from "./yarastringmatch/util";
import PreviewImage from "./PreviewImage";

require("codemirror/addon/display/autorefresh");
require("codemirror/mode/xml/xml");
require("codemirror/mode/javascript/javascript");
require("codemirror/mode/css/css");
require("codemirror/mode/htmlmixed/htmlmixed");
require("codemirror/mode/vbscript/vbscript");

export const PreviewTypes = {
  HEX: "hex",
  TEXT: "text",
  IMAGE: "image",
  EMAIL: "email"
};

interface PreviewSampleProps {
  previewType: string;
  sha1: string;
  typeDisplay?: string;
  fileType: string;
  fileSubtype: string;
  threatStatus?: number;
  searchAddress?: string;
  showOpenYaraRulesetButton?: boolean;
  initialRulesetName?: string;
  initialRuleName?: string;
  yaraTestRunResultId?: number;
  sampleSourceSha1?: string;
  relationshipId?: number;
  sourceID?: number;
}

const PreviewSample: FunctionComponent<PreviewSampleProps> = ({
  previewType,
  sha1,
  typeDisplay,
  fileType,
  fileSubtype,
  threatStatus,
  searchAddress,
  showOpenYaraRulesetButton = true,
  initialRulesetName,
  initialRuleName,
  yaraTestRunResultId,
  sampleSourceSha1,
  relationshipId,
  sourceID
}) => {
  const [beautify, setBeautify] = useState(false);
  const [showPotentiallyMaliciousFile, setShowPotentiallyMaliciousFile] =
    useState(false);

  const [selectedRulesetName, setSelectedRulesetName] = useState<
    MenuItem | undefined
  >(getMenuItemFromString(initialRulesetName));
  const [selectedRuleName, setSelectedRuleName] = useState<
    MenuItem | undefined
  >(getMenuItemFromString(initialRuleName));
  const [selectedVariableName, setSelectedVariableName] = useState<MenuItem>();

  const [currentHighlight, setCurrentHighlight] = useState({ currentIndex: 0 });

  const [editor, setEditor] = useState<Editor>();
  const [isInitialNavigationFinished, setIsInitialNavigationFinished] =
    useState(false);

  const { emailPreviewImage } = useEmailPreview(
    sha1,
    typeDisplay ?? "",
    sampleSourceSha1,
    relationshipId ?? 0
  );
  const { mutate: analyze } = useReanalyze();
  const {
    data: {
      dynamic_analysis: { rl_auxiliary_analysis: analysis = undefined } = {}
    } = {},
    refetch
  } = useGetSampleAnalysisStatus(sha1);

  const onReanalyze = () => {
    if (!sourceID) return;
    analyze({
      samples: [
        {
          hash_value: sha1,
          source_id: sourceID,
          relationship_id: relationshipId
        }
      ],
      integrations: ["rl_auxiliary_analysis"]
    });
    refetch();
  };

  const {
    hexPreviewData,
    textPreviewData,
    imagePreviewThumbnail,
    hasMoreData,
    loadMore,
    error: previewSampleError,
    isLoading: isPreviewSampleLoading
  } = usePreviewSampleData(
    sha1,
    previewType,
    searchAddress,
    threatStatus,
    beautify,
    showPotentiallyMaliciousFile
  );

  const yaraStringMatches = useYaraStringMatches(sha1, yaraTestRunResultId);

  const selectedRulesetId = useMemo(
    () =>
      yaraStringMatches.data?.string_matches?.find(
        item => item.ruleset_name === selectedRulesetName?.value
      )?.ruleset_id,
    [selectedRulesetName?.value, yaraStringMatches.data?.string_matches]
  );

  const { rulesetOptions, ruleOptions, variableOptions } = getFilterOptions(
    yaraStringMatches.data?.string_matches,
    selectedRulesetName?.value,
    selectedRuleName?.value
  );

  const matchesToHighlight = useMemo(() => {
    if (previewType !== PreviewTypes.HEX || searchAddress != null) {
      return [];
    }

    return yaraStringMatches.data?.string_matches
      ?.filter(item => item.ruleset_name === selectedRulesetName?.value)
      .map(item => item.rules)
      .flat()
      .filter(
        item =>
          selectedRuleName == null || item.rule_name === selectedRuleName?.value
      )
      .map(item => item.strings)
      .flat()
      .filter(
        item =>
          selectedVariableName == null ||
          item.string === selectedVariableName?.value
      )
      .map(item => item.match)
      .flat()
      .sort((a, b) => a.offset - b.offset);
  }, [
    yaraStringMatches.data?.string_matches,
    selectedRulesetName,
    selectedRuleName,
    selectedVariableName,
    previewType,
    searchAddress
  ]);

  const textMarks = useMemo(
    () => [
      ...calculateTextMarks(matchesToHighlight),
      ...calculateTextMarks(matchesToHighlight, 51, 1, 16, 16)
    ],
    [matchesToHighlight]
  );

  const currentlyHighlightedMarks = useMemo(() => {
    if (
      !matchesToHighlight?.length ||
      matchesToHighlight[currentHighlight.currentIndex] == null
    ) {
      return [];
    }

    return [
      ...calculateTextMarks([
        matchesToHighlight[currentHighlight.currentIndex]
      ]),
      ...calculateTextMarks(
        [matchesToHighlight[currentHighlight.currentIndex]],
        51,
        1,
        16,
        16
      )
    ];
  }, [matchesToHighlight, currentHighlight]);

  useEffect(() => {
    setCurrentHighlight({ currentIndex: 0 });
  }, [selectedRulesetName, selectedRuleName, selectedVariableName]);

  useEffect(() => {
    const prepareCodeMirrorContent = () => {
      if (!editor) {
        return;
      }
      const doc = editor.getDoc();
      if (
        previewType === PreviewTypes.HEX &&
        hexPreviewData?.searched === true
      ) {
        const hlLine = hexPreviewData?.searched_line;
        const hlColumn =
          hexPreviewData?.printable_column + hexPreviewData?.searched_column;
        doc.markText(
          {
            line: hlLine,
            ch: 0
          },
          {
            line: hlLine,
            ch: 100
          },
          { className: "highlight-searched-line" }
        );

        editor.scrollIntoView({ line: hlLine + 20, ch: hlColumn });
      }
    };
    if (!isPreviewSampleLoading) {
      prepareCodeMirrorContent();
    }
  }, [
    previewType,
    editor,
    hexPreviewData?.content,
    hexPreviewData?.searched,
    hexPreviewData?.searched_line,
    hexPreviewData?.searched_column,
    hexPreviewData?.printable_column,
    textPreviewData?.content,
    isPreviewSampleLoading
  ]);

  useEffect(() => {
    if (!editor || previewType !== PreviewTypes.HEX || searchAddress != null) {
      return;
    }

    const doc = editor.getDoc();

    doc
      ?.getAllMarks()
      ?.filter(mark => mark.className === "highlight-searched-line")
      .forEach(mark => mark.clear());

    textMarks?.forEach(mark => {
      doc.markText(
        {
          line: mark.line,
          ch: mark.start
        },
        {
          line: mark.line,
          ch: mark.end
        },
        { className: "highlight-searched-line" }
      );
    });
  }, [previewType, editor, searchAddress, textMarks, hexPreviewData?.content]);

  useEffect(() => {
    if (!editor || previewType !== PreviewTypes.HEX || searchAddress != null) {
      return;
    }

    const doc = editor.getDoc();

    doc
      ?.getAllMarks()
      ?.filter(mark => mark.className === "highlight-current-line")
      .forEach(mark => mark.clear());

    currentlyHighlightedMarks?.forEach(mark => {
      doc.markText(
        {
          line: mark.line,
          ch: mark.start
        },
        {
          line: mark.line,
          ch: mark.end
        },
        { className: "highlight-current-line" }
      );
    });

    const [firstCurrentlyHighlightedMark] = currentlyHighlightedMarks ?? [];
    if (firstCurrentlyHighlightedMark != null) {
      const lineCount = doc.lineCount();
      if (firstCurrentlyHighlightedMark?.line < lineCount) {
        editor.scrollIntoView({
          line: firstCurrentlyHighlightedMark.line,
          ch: 0
        });
      } else if (isInitialNavigationFinished) {
        loadMore();
      }
    }
  }, [
    currentlyHighlightedMarks,
    editor,
    previewType,
    searchAddress,
    hexPreviewData?.content,
    isInitialNavigationFinished,
    loadMore
  ]);

  const toggleBeautify = () =>
    setBeautify(previousBeautify => !previousBeautify);

  const getStatusText = () => {
    if (isPreviewSampleLoading) {
      return "Loading...";
    }
    if (hasMoreData && hexPreviewData?.searched !== true) {
      return (
        <>
          Loaded up to Offset{" "}
          {previewType === PreviewTypes.HEX
            ? hexPreviewData?.last_line_address
            : textPreviewData?.end_address}{" "}
          ...{" "}
          <span className="sample-preview__load-link" onClick={loadMore}>
            Click Here
          </span>{" "}
          to Load More Data
        </>
      );
    }
    return "Content Loaded";
  };

  const statusText = getStatusText();

  const showBeautifyButton =
    previewType === PreviewTypes.TEXT &&
    fileType === "Text" &&
    fileSubtype.toLowerCase() === "javascript";

  if (previewSampleError) {
    return (
      <div className="sample-preview__error">
        Error occurred while fetching sample preview.
      </div>
    );
  }

  const content = (
    <>
      {(previewType === PreviewTypes.HEX ||
        previewType === PreviewTypes.TEXT) && (
        <>
          <RenderWhen condition={previewType === PreviewTypes.HEX}>
            <YaraStringMatchControls
              selectedRulesetName={selectedRulesetName}
              selectedRulesetId={selectedRulesetId}
              selectedRuleName={selectedRuleName}
              selectedVariableName={selectedVariableName}
              currentHighlight={currentHighlight}
              totalMatchesToHighlight={matchesToHighlight?.length}
              setSelectedRulesetName={value => {
                setSelectedRulesetName(value);
                setSelectedRuleName(undefined);
                setSelectedVariableName(undefined);
              }}
              setSelectedRuleName={value => {
                setSelectedRuleName(value);
                setSelectedVariableName(undefined);
              }}
              setSelectedVariableName={setSelectedVariableName}
              setCurrentHighlight={value => {
                setIsInitialNavigationFinished(true);
                setCurrentHighlight(value);
              }}
              rulesetOptions={rulesetOptions}
              ruleOptions={ruleOptions}
              variableOptions={variableOptions}
              showOpenYaraRulesetButton={showOpenYaraRulesetButton}
              hasExtractedFilesWithYaraMatches={
                yaraStringMatches.data?.has_extracted_files_with_yara_matches
              }
              hash={sha1}
              sourceID={sourceID}
              relationshipId={relationshipId}
            />
          </RenderWhen>

          <div className="sample-preview__toolbar">
            {showBeautifyButton && (
              <span
                onClick={toggleBeautify}
                className="sample-preview__toolbar-btn"
              >
                {!beautify ? "Run Beautifier" : "See Original"}
              </span>
            )}
            {statusText && <span>{statusText}</span>}
          </div>
          <CodeMirror
            value={
              previewType === PreviewTypes.HEX
                ? hexPreviewData?.content
                : textPreviewData?.content
            }
            options={{
              mode: getCodeMirrorMode(
                previewType,
                fileSubtype.toLowerCase(),
                typeDisplay
              ),
              theme: "default",
              lineNumbers: true,
              lineWrapping: previewType === PreviewTypes.TEXT,
              readOnly: true,
              autoRefresh: true
            }}
            editorDidMount={currentEditor => {
              setEditor(currentEditor);
            }}
          />
        </>
      )}

      {previewType === PreviewTypes.IMAGE && (
        <PreviewImage imagePreviewPath={imagePreviewThumbnail}>
          <div className="sample-preview__image-warning">
            <p>Are you sure you want to view a potentially malicious file?</p>
            <p>
              <span
                className="sample-preview__load-link"
                onClick={() => setShowPotentiallyMaliciousFile(true)}
              >
                Click here
              </span>{" "}
              to view this image.
            </p>
          </div>
        </PreviewImage>
      )}

      {previewType === PreviewTypes.EMAIL && (
        <PreviewImage imagePreviewPath={emailPreviewImage ?? ""}>
          <div
            className="sample-preview__image-warning"
            data-testid="email_preview_image_placeholder"
          >
            {analysis?.display_status === "Processing" && <LoadingOverlay />}
            <p>
              <span className="sample-preview__load-link" onClick={onReanalyze}>
                Send sample for Auxiliary Analysis
              </span>{" "}
              to generate the email preview.
            </p>
          </div>
        </PreviewImage>
      )}
    </>
  );

  return (
    <VisualizationLoadingHandler
      isLoading={
        (isPreviewSampleLoading ?? false) || yaraStringMatches.isLoading
      }
      Component={content}
    />
  );
};

export default PreviewSample;
